import * as React from 'react';
import authService from './api-authorization/AuthorizeService';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';

interface RunSheetDriverUpdateOffice {
    loading: boolean;
    errors: any;
}

interface RunSheetDriverOfficeProps {
    job: Job;
    updateJob(job: Job): void;
    save(): void;
}

interface Job {
    runsheetId: number;
    scheduleDateFormatted: string;
    truck: string;
    jobId: number;
    siteAddress: string;
    updateTypeId: number;     //1=Callout, 2=Plan Changes
    estimatedTime: string;
    departureTime: string;
    siteArrivalTime: string;
    downTime: string;
    siteCompletionTime: string;
    driverNotes: string;
    chargeCalloutFee: number;
    noCalloutInvoiceNotes: string;
    calloutNotes: string;
    planChangesReason: string;
    officeNotes: string;
    items: JobItem[];
}

interface JobItem {
    id: number;
    typeId: number;
    type: string;
    item: string;
    itemId: number;
    hirePeriod: number;
    unitNumber: string; //if site address has a unit number
    quantityTotal: number;
    quantity: number;
    quantityScheduled: number;
    quantityType: string;
    price: number;
    overhireRate: number;
    isKwikstage: boolean;
    kwikstageItems: KwikstageScheduleItem[];
}

interface KwikstageScheduleItem {
    scheduleKwikstageId: number;
    jobQuoteItemCalculationId: number;
    starterBayId: number;
    starterBay: string;
    liftId: number;
    lift: string;
    baysTotal: number;
    baysComplete: number;
}

export default class RunSheetDriverOffice extends React.Component<RunSheetDriverOfficeProps, RunSheetDriverUpdateOffice> {
    constructor(props: any) {
        super(props);

        this.state = {
            loading: false,
            errors: {}
        };
    }

    handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors["times"];
            if (e.target.name === "planChangesReason") {
                delete errors["reason"];
            }
            this.setState({ errors: errors });
        }

        let change: any = {};
        change = this.props.job;
        change[e.target.name] = e.target.value;
        this.setState({ errors: errors })
        this.props.updateJob(change);
    }

    onCellChange = (e: any, itemIndex: number) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors["items"];
            this.setState({ errors: errors });
        }

        //update item that has changed
        var job = this.props.job;
        var item = job.items[itemIndex];
        let newItem: any = {};
        newItem = item;
        newItem[e.target.name] = e.target.value;

        this.props.updateJob(job);
    }

    onCellQtyTotalChange = (e: any, itemIndex: number) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors["items"];
            this.setState({ errors: errors });
        }

        var qtyTotal = e.target.value * 1;
        this.calcPrice(itemIndex, qtyTotal);
    }

    async calcPrice(itemIndex: number, quantity: number) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //update item that has changed
        var job = this.props.job;
        var item = job.items[itemIndex];
        item.quantityTotal = quantity;

        axios.get('api/Job/CalculatePlanChangesPrice?JobId=' + job.jobId + '&ScheduleId=' + item.id + '&Quantity=' + quantity + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
            .then(res => {
                if (res.data.amount > 0) {
                    //only update the quote prices if there is a pricelist amount
                    item.price = res.data.amount;
                    item.overhireRate = res.data.overhireRate;
                }

                this.props.updateJob(job);
            })
            .catch(error => {
                toast.error(error.message);
            });
    }

    onKsCellChange = (e: any, itemIndex: number, ksItemIndex: number) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors["items"];
            this.setState({ errors: errors });
        }

        //update item that has changed
        var job = this.props.job;
        var item = job.items[itemIndex];
        var ksItem = item.kwikstageItems[ksItemIndex];
        let newItem: any = {};
        newItem = ksItem;
        newItem[e.target.name] = e.target.value;

        this.props.updateJob(job);
    }

    onKsCellQtyTotalChange = (e: any, itemIndex: number, ksItemIndex: number) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors["items"];
            this.setState({ errors: errors });
        }

        var qtyTotal = e.target.value * 1;
        this.calcKsPrice(itemIndex, ksItemIndex, qtyTotal);
    }

    async calcKsPrice(itemIndex: number, ksItemIndex: number, quantity: number) {
        const token = await authService.getAccessToken();

        //update item that has changed
        var job = this.props.job;
        var item = job.items[itemIndex];
        var ksItems = item.kwikstageItems;
        var ksItem = ksItems[ksItemIndex];
        ksItem.baysTotal = quantity;

        var calcKsPrice = {
            scheduleId: item.id,
            scheduleKwikstageId: ksItem.scheduleKwikstageId,
            ksItems: ksItems
        };

        axios.post('api/Job/CalculatePlanChangesKwikstagePrice', calcKsPrice, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
            .then(res => {
                if (res.data.amount > 0) {
                    //only update the quote prices if there is a pricelist amount
                    item.price = res.data.amount;
                    item.overhireRate = res.data.overhireRate;
                }

                this.props.updateJob(job);
            })
            .catch(error => {
                toast.error(error.message);
            });
    }

    validate = () => {
        let errors: any = {};
        let formIsValid = true;
        var errorStringTimes = "";
        var error = false;

        let job = this.props.job;

        //Check Times
        if (job.departureTime == "") {
            errorStringTimes += "Departure Time, ";
            formIsValid = false;
        }
        if (job.siteArrivalTime == "") {
            errorStringTimes += "Site Arrival Time, ";
            formIsValid = false;
        }
        if (job.downTime && !DateHelpers.validTimeAmount(job.downTime)) {
            errorStringTimes += "Valid Down Time, ";
            formIsValid = false;
        }
        if (job.siteCompletionTime == "") {
            errorStringTimes += "Site Completion Time, ";
            formIsValid = false;
        }

        if (job.departureTime != "" && job.siteArrivalTime != "") {
            if (DateHelpers.validTimeAmount(job.siteArrivalTime) && DateHelpers.validTimeAmount(job.departureTime)) {
                if (!DateHelpers.compareTimeGreater(job.departureTime, job.siteArrivalTime)) {
                    errorStringTimes += "Arrival Time needs to be > than Departure Time \n";
                }
            } else {
                errorStringTimes += "Departure Time and Arrival Time need to be valid times \n";
            }
        }

        if (job.siteCompletionTime != "" && job.siteArrivalTime != "") {
            if (DateHelpers.validTimeAmount(job.siteArrivalTime) && DateHelpers.validTimeAmount(job.siteCompletionTime)) {
                if (!DateHelpers.compareTimeGreater(job.siteArrivalTime, job.siteCompletionTime)) {
                    errorStringTimes += "Completion Time needs to be > than Arrival Time \n";
                }
            } else {
                errorStringTimes += "Arrival Time and Completion Time need to be valid times \n";
            }
        }

        if (errorStringTimes != "") {
            formIsValid = false;
            errors["times"] = "Please enter the following times: " + errorStringTimes.substring(0, errorStringTimes.length - 2);
        }

        if (job.officeNotes === "") {
            formIsValid = false;
            errors["reason"] = "Reason for change is required";
        }

        //Check Quantity and Price
        var errorString = "Please enter the following values: ";
        var rowNumber = 0;
        let items = job.items;

        for (var i = 0; i < items.length; i++) {
            let fields = items[i];
            rowNumber += 1;
            var newErrorString = "";

            //Check Non-Kwikstage Items first
            if (!fields.isKwikstage) {
                //check normal quantity
                let quantityTotal = fields.quantityTotal * 1;
                if (quantityTotal < 1) {
                    error = true;
                    newErrorString += "Quantity Total, ";
                }

                let quantity = fields.quantity * 1;
                if (quantity < 1) {
                    error = true;
                    newErrorString += "Quantity, ";
                }

                if (quantity > quantityTotal) {
                    error = true;
                    newErrorString += "Quantity must be <= Quantity Total, ";
                }
            }

            //Check Prices
            if (fields.price < 0.01) {
                error = true;
                newErrorString += "Price, ";
            }

            if (fields.overhireRate < 0.01) {
                error = true;
                newErrorString += "Overhire Rate, ";
            }
            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
            }

            //Check Kwikstage Items (as these are on the next row/s)
            if (fields.isKwikstage) {
                //check kwikstage quantities
                for (var k = 0; k < fields.kwikstageItems.length; k++) {
                    rowNumber += 1;
                    var newErrorString = "";
                    var field = fields.kwikstageItems[k];

                    let baysTotal = field.baysTotal * 1;
                    if (baysTotal < 1) {
                        newErrorString += "Bays Total, ";
                        error = true;
                    }

                    let baysComplete = field.baysComplete * 1;
                    if (baysComplete < 1) {
                        newErrorString += "Bays Complete, ";
                        error = true;
                    }

                    if (baysComplete > baysTotal) {
                        newErrorString += "Bays Complete must be <= Bays Total, ";
                        error = true;
                    }

                    if (newErrorString != "") {
                        errorString += "\n"
                        errorString += "Row " + rowNumber + ": " + newErrorString;
                    }
                }
            }
        }
        if (error) {
            formIsValid = false;
            errorString = errorString.substring(0, errorString.length - 2);
            errors["items"] = errorString;
        }
        this.setState({ errors: errors });
        return formIsValid;
    }

    save = () => {
        if (this.validate()) {
            this.props.save();
        } else {
            toast.error("Please fix the validation errors on screen before saving!");
        }
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();


        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        let job = this.props.job;

        return (
            <div>
                 <div className="marginLeft10">
                    <h5>
                        <span>{job.truck}</span>
                        <span className="marginLeft20">{job.jobId}</span>
                        <span className="marginLeft20">{job.siteAddress}</span>
                    </h5>
                </div>
                <div className="marginLeft10">
                    {job.driverNotes}
                </div>
                <div>
                    <label className="input-group--inline" htmlFor="estimatedTime">
                        <span className="label-small">Est. Time:</span>
                        <span className="marginLeft10" id="estimatedTime">{job.estimatedTime}</span>
                    </label>
                    <label className="input-group--inline" htmlFor="departureTime">
                        <span className="label-small">Departure:</span>
                        <input className="marginLeft10" id="departureTime" name="departureTime" type="time" value={job.departureTime} onChange={(e) => this.handleChange(e)} />
                    </label>
                    <label className="input-group--inline" htmlFor="siteArrivalTime">
                        <span className="label-small">Arrival:</span>
                        <input className="marginLeft10" id="siteArrivalTime" name="siteArrivalTime" type="time" value={job.siteArrivalTime} onChange={(e) => this.handleChange(e)} />
                    </label>
                    <label className="input-group--inline" htmlFor="downTime">
                        <span className="label-small">Down Time:</span>
                        <input className="form-control runsheet__timeSize marginLeft10" type='text' id="downTime" name="downTime" pattern="(0?[0-9]|1[0-2]):[0-5][0-9]$" value={job.downTime} onChange={(e) => this.handleChange(e)} />
                    </label>
                    <label className="input-group--inline" htmlFor="siteCompletionTime">
                        <span className="label-small">Completion:</span>
                        <input className="marginLeft10" id="siteCompletionTime" name="siteCompletionTime" type="time" value={job.siteCompletionTime} onChange={(e) => this.handleChange(e)} />
                    </label>
                </div>
                <div className={this.state.errors["times"] ? "" : "hidden"}><span className="label errors">{this.state.errors["times"]}</span></div>
                <div>
                    <table className='runsheetDriverTable runsheetDriverTable--width table tableColours-header'>
                        <thead>
                            <tr>
                                <th className="hidden">Id</th>
                                <th>Unit</th>
                                <th>Item</th>
                                <th className="textalignright">Qty Total</th>
                                <th className="textalignright">Qty</th>
                                <th className="textalignright">Price</th>
                                <th className="textalignright">Overhire Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {job.items.map((item, itemIndex) =>
                                <React.Fragment key={itemIndex}>
                                    <tr className="runsheetRow">
                                        <td className="hidden">{item.id}</td>
                                        <td className="table__text--align">{item.unitNumber === "" ? "" : "Unit " + item.unitNumber}</td>
                                        <td className="table__text--align table--col-l">{item.item}</td>
                                        <td className="table__text--align table--col-m">
                                            <input type='number' min="0" step="1" className={item.isKwikstage ? "hidden" : "form-control textalignright"} id="quantityTotal" name="quantityTotal" value={item.quantityTotal} onChange={(e) => this.onCellQtyTotalChange(e, itemIndex)} disabled />
                                        </td>
                                        <td className="table__text--align table--col-m">
                                            <input type='number' min="0" step="1" className={item.isKwikstage ? "hidden" : "form-control textalignright"} id="quantity" name="quantity" value={item.quantity} onChange={(e) => this.onCellChange(e, itemIndex)} />
                                        </td>
                                        <td className="table__text--align table--col-m">
                                            <input type='number' min="0" step="0.01" className="form-control textalignright" id="price" name="price" value={item.price} onChange={(e) => this.onCellChange(e, itemIndex)} disabled />
                                        </td>
                                        <td className="table__text--align table--col-m">
                                            <input type="number" min="0" step="0.01" className="form-control textalignright" id="overhireRate" name="overhireRate" value={item.overhireRate} onChange={(e) => this.onCellChange(e, itemIndex)} disabled />
                                        </td>
                                    </tr >
                                    <tr className={item.kwikstageItems.length > 0 ? "runsheetRow" : "hidden"}>
                                        <td colSpan={6}>
                                            <table>
                                                <tbody>
                                                    {item.kwikstageItems.map((ksItem, ksIndex) =>
                                                        <tr key={itemIndex} className={item.typeId === 1 ? "tableColours--install" : item.typeId === 2 ? "tableColours--pickup" : item.typeId === 3 ? "tableColours--assist" : "tableColours--cleaning"}>
                                                            <td className="hidden">{ksItem.scheduleKwikstageId}</td>
                                                            <td className="table__text--align">{ksItem.starterBay} - {ksItem.lift}</td>
                                                            <td className="table__text--align table--col-m">
                                                                <input type='number' min="0" step="1" className="form-control textalignright" id="baysTotal" name="baysTotal" value={ksItem.baysTotal} onChange={(e) => this.onKsCellQtyTotalChange(e, itemIndex, ksIndex)} />
                                                            </td>
                                                            <td className="table__text--align table--col-m">
                                                                <input type='number' min="0" step="1" className="form-control textalignright" id="baysComplete" name="baysComplete" value={ksItem.baysComplete} onChange={(e) => this.onKsCellChange(e, itemIndex, ksIndex)} />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            )}
                        </tbody>

                    </table>
                    {this.state.errors["items"] ?
                        (this.state.errors["items"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
                <div>
                    <label className="input-group--nowrap input-group--nowrap__max--width" htmlFor="officeNotes">
                        <span className="label">Reason for change</span>
                        <textarea className="input allocate__teamnotes" id="officeNotes" name="officeNotes" value={job.officeNotes} onChange={(e) => this.handleChange(e)}></textarea>
                    </label>
                    <span className="label errors">{this.state.errors["reason"]}</span>
                </div>
                <button className="defaultbutton-next" type="button" onClick={this.save}>Save</button>
            </div>


        );

    }

};

